import { z } from 'zod';

import { auditSchema, countSchema, positiveIntSchema, timestampSchema } from './common';
import {
  groupIdSchema,
  matchIdSchema,
  phaseIdSchema,
  roundKeySchema,
  teamIdSchema,
  tournamentIdSchema,
  uidSchema,
} from './ids';

/**
 * `pending` — draw not yet decided (KO slot waiting for a preceding match)
 * `bye`     — free pass, the opposing side advances without playing
 */
export const matchSideSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('team'),
    teamId: teamIdSchema,
    /**
     * Where this participant came from, e.g. `winner:<matchId>` — kept when the
     * engine fills a knockout slot so the slot can be *re*-decided after a
     * result correction. Absent for a drawn or seeded entry, which no preceding
     * match determines.
     */
    sourceRef: z.string().trim().min(1).max(128).optional(),
  }),
  z.object({ kind: z.literal('bye') }),
  z.object({
    kind: z.literal('pending'),
    /** Where the participant will come from, e.g. `winner:<matchId>`. */
    sourceRef: z.string().trim().min(1).max(128),
    /** Human readable placeholder for the bracket view, e.g. "Sieger VF 1". */
    label: z.string().trim().min(1).max(64),
  }),
]);

export type MatchSide = z.infer<typeof matchSideSchema>;

export const matchStatusSchema = z.enum([
  'pending', // wartet
  'called', // aufgerufen
  'running', // läuft
  'completed', // beendet
  'conflict', // Konflikt
]);

export type MatchStatus = z.infer<typeof matchStatusSchema>;

/** Legs and, in set mode, sets won by each side. */
export const matchScoreSchema = z.object({
  legs: z.object({ home: countSchema.max(999), away: countSchema.max(999) }),
  sets: z.object({ home: countSchema.max(99), away: countSchema.max(99) }).optional(),
});

export type MatchScore = z.infer<typeof matchScoreSchema>;

/**
 * One dart in detailed entry mode. `segment` 25 is the bull; `multiplier` 2 on
 * the bull is the 50 (double bull).
 */
export const dartSchema = z.object({
  segment: countSchema.max(25),
  multiplier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
});

export type Dart = z.infer<typeof dartSchema>;

/**
 * One recorded turn (Aufnahme) of the live counter — the *raw* throw.
 *
 * This is the source data of a match: whose turn it was, the running score,
 * busts, legs and the winner are all recomputed from the ordered turn log by
 * the X01 engine, never stored redundantly. Storing the thrown points (not the
 * net-after-bust) keeps the log faithful for statistics.
 */
export const recordedTurnSchema = z.object({
  points: countSchema.max(180),
  finishedOnDouble: z.boolean(),
  darts: z.array(dartSchema).max(3).optional(),
});

export type RecordedTurn = z.infer<typeof recordedTurnSchema>;

export const entryModeSchema = z.enum(['sum', 'darts']);
export type EntryMode = z.infer<typeof entryModeSchema>;

/**
 * The live scoring state at `match.counter`: the game configuration frozen at
 * start (so a later settings change cannot rewrite a running game), who threw
 * first, and the append-only turn log.
 */
export const matchCounterSchema = z.object({
  startScore: positiveIntSchema.max(1701),
  doubleOut: z.boolean(),
  entryMode: entryModeSchema,
  startingSide: z.enum(['home', 'away']),
  startedBy: uidSchema,
  turns: z.array(recordedTurnSchema).max(2000),
});

export type MatchCounter = z.infer<typeof matchCounterSchema>;

/** One team captain's confirmation of the final result. */
export const matchConfirmationSchema = z.object({
  uid: uidSchema,
  at: timestampSchema,
});

export type MatchConfirmation = z.infer<typeof matchConfirmationSchema>;

export const resultSourceSchema = z.enum([
  'agreement', // beide Meldungen identisch (Altfluss) / beide Bestätigungen
  'director', // von der Turnierleitung gesetzt oder korrigiert
  'walkover', // Freilos oder Nichtantreten
]);

export type ResultSource = z.infer<typeof resultSourceSchema>;

export const matchResultSchema = z.object({
  winnerTeamId: teamIdSchema.nullable(),
  loserTeamId: teamIdSchema.nullable(),
  score: matchScoreSchema,
  source: resultSourceSchema,
  confirmedAt: timestampSchema,
});

export type MatchResult = z.infer<typeof matchResultSchema>;

/** Position of a match inside a knockout bracket. */
export const bracketSlotSchema = z.object({
  /** 0 = first round played, counting upwards towards the final. */
  round: countSchema.max(32),
  /** Position within the round, left to right in the bracket view. */
  index: countSchema.max(1024),
});

export type BracketSlot = z.infer<typeof bracketSlotSchema>;

/**
 * A match in `tournaments/{tournamentId}/matches/{matchId}`.
 *
 * Note what is *absent*: the applicable match rules. They are resolved on read
 * from the tournament settings via `roundKey` and `phaseId`, so the settings
 * stay the single source of truth and no stale copy can contradict them.
 */
export const matchSchema = z.object({
  id: matchIdSchema,
  tournamentId: tournamentIdSchema,

  phaseId: phaseIdSchema,
  /** Most specific key for resolving the match rules, e.g. `ko.finale`. */
  roundKey: roundKeySchema,
  /** Only set within a group phase. */
  groupId: groupIdSchema.nullable(),
  /** Only set within a knockout bracket. */
  bracketSlot: bracketSlotSchema.nullable(),
  /** Sort order for the schedule view; assigned when the phase is planned. */
  order: countSchema.max(100_000),

  home: matchSideSchema,
  away: matchSideSchema,

  boardNumber: positiveIntSchema.max(999).nullable(),
  status: matchStatusSchema,

  /** Live X01 scoring state; `null` until the counter is started at the board. */
  counter: matchCounterSchema.nullable(),
  /** Both team captains must confirm before the result is verified. */
  confirmations: z.object({
    home: matchConfirmationSchema.optional(),
    away: matchConfirmationSchema.optional(),
  }),

  result: matchResultSchema.nullable(),

  calledAt: timestampSchema.nullable(),
  startedAt: timestampSchema.nullable(),
  finishedAt: timestampSchema.nullable(),

  ...auditSchema.shape,
});

export type Match = z.infer<typeof matchSchema>;
